import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { CarClassApi } from '../../shared/models/car-class.models';

/**
 * Request payload for /buyer/sort
 * Arrays are optional so we can omit them when no filter is selected.
 */
export interface GetSortedItemsRequest {
  sortBy: string;
  categoryIds?: number[];
  subcategoryIds?: number[];
  vendorIds?: number[];
  classificationIds?: number[];
  page: number;
  size: number;
  low: number;
  high?: number; // ✅ make optional
  name?: string;
}

/** Utility: omit null/undefined/empty strings and empty arrays */
function clean<T extends Record<string, any>>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) =>
      v !== undefined &&
      v !== null &&
      v !== '' &&
      !(Array.isArray(v) && v.length === 0)
    )
  ) as Partial<T>;
}

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) { }

  /** New arrivals + trending */
  getNewArrivalsAndTrending(): Observable<any> {
    return this.http.get(`${this.baseUrl}/buyer/new-arrivals-and-trending`);
  }

  getThreeProductsOnDashboard(): Observable<any> {
    return this.http.get(`${this.baseUrl}/buyer/items/random-by-category`);
  }

  /** All items (paged) */
  getAllItems(page: number, size: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/items/getAll`, {
      params: new HttpParams().set('page', page).set('size', size),
    });
  }

  /** Categories for left filter */
  getAllCategories(): Observable<any> {
    return this.http.get(`${this.baseUrl}/categories/getAllWithIds`);
  }

  /** Filter by one category (paged) */
  getItemsByCategory(categoryId: number, page: number, size: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/buyer/filterItemsByCategory`, {
      params: new HttpParams()
        .set('categoryId', categoryId)
        .set('page', page)
        .set('size', size),
    });
  }

  /** Filter by multiple subcategories via query params */
  getItemsBySubcategories(subcategoryIds: number[]): Observable<any> {
    let params = new HttpParams();
    for (const id of subcategoryIds) params = params.append('subcategoryIds', id);
    return this.http.get(`${this.baseUrl}/buyer/filterItemsBySubCategories`, { params });
  }

  /** Single item detail */
  getItem(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/items/${id}`);
  }

  /**
   * Core listing API (sorted + filters)
   * Uses clean() to omit empty/undefined fields so backend treats them as "no filter".
   */
  getSortedItems(req: GetSortedItemsRequest): Observable<any> {
    const body = clean({
      sortBy: req.sortBy,
      categoryIds: req.categoryIds,
      vendorIds: req.vendorIds,
      classificationIds: req.classificationIds,
      subcategoryIds: req.subcategoryIds,
      page: req.page,
      size: req.size,
      low: req.low,
      high: req.high,
      name: req.name?.trim() || undefined,
    });

    return this.http.post(`${this.baseUrl}/buyer/sort`, body);
  }

  /** Price-only filter (alternative endpoint) */
  getPriceFilterItems(low: number, high: number, page: number, size: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/buyer/price-filter`, {
      params: new HttpParams()
        .set('low', low)
        .set('high', high)
        .set('page', page)
        .set('size', size),
    });
  }

  /** Exclusion/combination validation */
  getCombinationValue(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/exclusion/validate-combination`, data);
  }

  getSearchResults(name: string): Observable<any> {
    const params = new HttpParams().set('name', name);
    return this.http.get(`${this.baseUrl}/buyer/search-products?${params}`);
  }

  getvendors(): Observable<any> {
    return this.http.get(`${this.baseUrl}/vendors/getAll`);
  }


  getClassifications(): Observable<CarClassApi[]> {
    return this.http
      .get<{ content: CarClassApi[] }>(`${this.baseUrl}/classifications?page=0&size=10`)
      .pipe(map(res => res?.content ?? []));
  }

}
